import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useVerifiedStatus } from '../hooks/useVerifiedStatus';
import { copyToClipboard } from '../utils/clipboard';
import { buildReferralLink } from '../utils/referral';

export default function Invite() {
  const { user, loading: authLoading } = useAuth();
  const { verified, loading: verifiedLoading } = useVerifiedStatus();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [affiliateCode, setAffiliateCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copyStatusMessage, setCopyStatusMessage] = useState('');

  const referralLink = affiliateCode ? buildReferralLink(affiliateCode) : null;

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    // Gate: only verified users can access the Invite page
    if (!verifiedLoading && !verified) {
      navigate('/dashboard');
      return;
    }

    if (!verifiedLoading) {
      fetchReferralInfo();
    }
  }, [authLoading, user, navigate, verified, verifiedLoading]);

  const fetchReferralInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/referral-info`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        if (response.status === 404) {
          setError('AFFILIATE_CODE_UNAVAILABLE');
        } else {
          setError('FETCH_ERROR');
        }
        setLoading(false);
        return;
      }

      const data = await response.json();
      if (data.affiliate_code) {
        setAffiliateCode(data.affiliate_code);
      } else {
        setError('AFFILIATE_CODE_UNAVAILABLE');
      }
    } catch (err) {
      console.error('Failed to fetch referral info:', err);
      setError('FETCH_ERROR');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!referralLink) return;
    const success = await copyToClipboard(referralLink);
    if (success) {
      setCopied(true);
      setCopyStatusMessage('Link copied to clipboard!');
      setTimeout(() => {
        setCopied(false);
        setCopyStatusMessage('');
      }, 2000);
    } else {
      setCopyStatusMessage('Failed to copy link');
      setTimeout(() => setCopyStatusMessage(''), 2000);
    }
  };

  const handleShare = () => {
    if (!navigator.share || !referralLink) return;
    try {
      navigator.share({
        title: 'Join Wellis',
        text: 'Join my Wellis team using this referral link.',
        url: referralLink,
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Share error:', err);
      }
    }
  };

  return (
    <div className="min-h-screen p-margin-mobile pt-24 pb-32" style={{ backgroundColor: '#1a3a2a' }}>
      <div className="max-w-container-max mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-headline-lg mb-2" style={{ color: '#c9a961' }}>Invite New Member</h1>
          <p className="text-body-md" style={{ color: '#b8a68f' }}>
            Share your referral link to grow your network
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <p style={{ color: '#b8a68f' }}>Loading your referral link...</p>
          </div>
        ) : error ? (
          <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
            <p className="text-body-md" style={{ color: '#b8a68f' }}>
              Unable to generate your referral link. Please contact support.
            </p>
          </div>
        ) : (
          <>
            {/* Referral Code Card */}
            <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <h2 className="text-headline-sm font-bold mb-6" style={{ color: '#c9a961' }}>Your Referral Code</h2>

              <div className="border rounded-lg p-6 mb-6" style={{ backgroundColor: '#152214', borderColor: '#c9a961' }}>
                <div className="text-body-sm mb-2" style={{ color: '#b8a68f' }}>Code</div>
                <div className="text-headline-md font-bold font-mono break-all" style={{ color: '#c9a961' }}>
                  {affiliateCode}
                </div>
                <p className="text-body-xs mt-3" style={{ color: '#b8a68f' }}>
                  Share this code to let others know they were referred by you
                </p>
              </div>
            </div>

            {/* Referral Link Card */}
            <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <h2 className="text-headline-sm font-bold mb-6" style={{ color: '#c9a961' }}>Your Referral Link</h2>

              <div className="border rounded-lg p-4 mb-6 overflow-x-auto" style={{ backgroundColor: '#152214', borderColor: '#c9a961' }}>
                <code className="text-body-sm break-all" style={{ color: '#b8a68f' }}>{referralLink}</code>
              </div>

              <div className="flex gap-3 mb-6">
                <button
                  onClick={handleCopyLink}
                  disabled={!referralLink}
                  className="px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed text-black"
                  style={{ backgroundColor: '#c9a961' }}
                >
                  {copied ? '✓ Copied!' : '📋 Copy Link'}
                </button>

                {navigator.share && (
                  <button
                    onClick={handleShare}
                    className="border px-6 py-3 rounded-lg font-medium transition-colors text-black"
                    style={{ backgroundColor: '#c9a961', borderColor: '#c9a961' }}
                  >
                    📤 Share
                  </button>
                )}
              </div>

              {copyStatusMessage && (
                <div aria-live="polite" className="text-body-sm" style={{ color: '#c9a961' }}>
                  {copyStatusMessage}
                </div>
              )}

              <p className="text-body-sm" style={{ color: '#b8a68f' }}>
                When someone clicks this link, they'll land on the signup page with your referral code pre-filled.
              </p>
            </div>

            {/* QR Code Card */}
            <div className="border rounded-lg p-6 mb-8" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <h2 className="text-headline-sm font-bold mb-6" style={{ color: '#c9a961' }}>QR Code</h2>

              <div className="flex flex-col items-center">
                {referralLink && (
                  <div className="mb-4">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(referralLink)}`}
                      alt="Referral QR code"
                      className="border rounded"
                      style={{ borderColor: '#c9a961' }}
                    />
                  </div>
                )}
                <p className="text-body-sm text-center" style={{ color: '#b8a68f' }}>
                  Scan this code to open your referral link on mobile
                </p>
              </div>
            </div>

            {/* Instructions */}
            <div className="border rounded-lg p-6" style={{ backgroundColor: '#0d2818', borderColor: '#c9a961' }}>
              <h3 className="text-headline-sm font-bold mb-4" style={{ color: '#c9a961' }}>How It Works</h3>
              <ul className="space-y-3">
                <li className="flex gap-3">
                  <span className="font-bold flex-shrink-0" style={{ color: '#c9a961' }}>1</span>
                  <span className="text-body-sm" style={{ color: '#b8a68f' }}>
                    Share your referral code or link with friends, family, and colleagues
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold flex-shrink-0" style={{ color: '#c9a961' }}>2</span>
                  <span className="text-body-sm" style={{ color: '#b8a68f' }}>
                    When they sign up using your code, they'll be added to your downline
                  </span>
                </li>
                <li className="flex gap-3">
                  <span className="font-bold flex-shrink-0" style={{ color: '#c9a961' }}>3</span>
                  <span className="text-body-sm" style={{ color: '#b8a68f' }}>
                    You'll earn commissions on their purchases and growth
                  </span>
                </li>
              </ul>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
